import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import DataTable from '../components/DataTable';
import { Tenant, Column, PaginationParams } from '../types';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { validateName, validateUrl, validateCode } from '../utils/validation';
import { tenantService } from '../services/tenantService';
import { mapApiTenantToUi, mapUiToCreateTenantApiModel, mapUiToUpdateTenantApiModel, mapUiToUpdateTenantApiModelPatch } from '../utils/tenantMapper';
import StatusChip from '../components/StatusChip';
import { PERMISSIONS } from '../utils/permissionAccess';

interface TenantFormData {
  code: string;
  name: string;
  frontendServer: string;
  backendApi: string;
  middlewareApi: string;
  status: 'Active' | 'Inactive' | 'Pending' | 'Maintenance';
}

const Tenants: React.FC = () => {
  const { tenants, setTenants } = useData();
  const { tokens, login, user } = useAuth();
  
  // Get user permissions
  const userPermissions = user?.permissions || [];
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
  });

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    code: '',
    name: '',
    frontendServer: '',
    backendApi: '',
    middlewareApi: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState<Partial<TenantFormData>>({});

  // Load tenants from API
  const loadTenants = async (params: PaginationParams = pagination) => {
    try {
      setLoading(true);
      setError(null);

      // Check if tenant endpoints are available
      if (!tenantService.isAuthenticated()) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Call the actual API endpoint
      const response = await tenantService.getTenants(params);

      // Handle flexible response shapes (e.g., { data: { items: [...] } } or { items: [...] } or array)
      const topLevel = (response as any)?.data ?? response;
      const items = Array.isArray(topLevel)
        ? topLevel
        : (topLevel?.items ?? topLevel?.data ?? []);

      if (Array.isArray(items)) {
        const mappedTenants = items.map(mapApiTenantToUi);
        setTenants(mappedTenants);
        setError(null);
      } else {
        setTenants([]);
        setError((response as any)?.errorDescription || 'Failed to load tenants');
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError('Failed to load tenants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initialize service and load tenants on component mount
  useEffect(() => {
    // Initialize the tenant service with current tokens
    tenantService.initialize(tokens, login);
    
    // Load tenants if we have tokens
    if (tokens) {
      loadTenants();
    } else {
      setLoading(false);
      setError('Authentication required. Please log in.');
    }
  }, [tokens, login]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns: Column[] = [
    { id: 'code', label: 'Code', minWidth: 150, width: '15%', sortable: true },
    { id: 'name', label: 'Name', minWidth: 150, width: '15%', sortable: true },
    { id: 'frontendServer', label: 'Frontend Server', minWidth: 200, width: '22%', sortable: true },
    { id: 'backendApi', label: 'Backend API', minWidth: 200, width: '22%', sortable: true },
    { id: 'middlewareApi', label: 'Middleware API', minWidth: 200, width: '18%', sortable: true },
    { 
      id: 'status', 
      label: 'Status', 
      minWidth: 100, 
      width: '8%', 
      sortable: true,
      render: (value: string) => <StatusChip status={value as 'Active' | 'Inactive' | 'Pending' | 'Maintenance'} />,
    },
  ];

  const handleAdd = (): void => {
    setEditingTenant(null);
    setFormData({
      code: '',
      name: '',
      frontendServer: '',
      backendApi: '',
      middlewareApi: '',
      status: 'Active',
    });
    setOpenModal(true);
  };

  const handleEdit = (tenant: Tenant): void => {
    setEditingTenant(tenant);
    setFormData({
      code: tenant.code || '',
      name: tenant.name,
      frontendServer: tenant.frontendServer,
      backendApi: tenant.backendApi,
      middlewareApi: tenant.middlewareApi,
      status: tenant.status,
    });
    setOpenModal(true);
  };

  const handleDelete = async (tenant: Tenant): Promise<void> => {
    try {
      const response = await tenantService.deleteTenant(tenant.id);
      if (response.succeeded) {
        showSnackbar('Tenant deleted successfully', 'success');
        loadTenants(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to delete tenant', 'error');
      }
    } catch (err) {
      console.error('Error deleting tenant:', err);
      showSnackbar('Failed to delete tenant. Please try again.', 'error');
    }
  };

  const handleToggleStatus = async (tenant: Tenant): Promise<void> => {
    try {
      const newStatus = tenant.status === 'Active' ? false : true;
      const response = await tenantService.toggleTenantStatus(tenant.id, newStatus);
      if (response.succeeded) {
        showSnackbar(`Tenant ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        loadTenants(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to update tenant status', 'error');
      }
    } catch (err) {
      console.error('Error updating tenant status:', err);
      showSnackbar('Failed to update tenant status. Please try again.', 'error');
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }
    
    try {
      if (editingTenant) {
        // Update existing tenant - use PATCH with only changed fields
        const updateData = mapUiToUpdateTenantApiModelPatch(editingTenant, formData);
        const response = await tenantService.updateTenant(editingTenant.id, updateData);
        
        if (response.succeeded) {
          showSnackbar('Tenant updated successfully', 'success');
          loadTenants(); // Reload the list
          setOpenModal(false);
          setErrors({});
        } else {
          showSnackbar(response.errorDescription || 'Failed to update tenant', 'error');
        }
      } else {
        // Create new tenant
        const createData = mapUiToCreateTenantApiModel(formData);
        console.log('Creating tenant with data:', createData); // Debug: verify code is included
        const response = await tenantService.createTenant(createData);
        
        if (response.succeeded) {
          showSnackbar('Tenant created successfully', 'success');
          loadTenants(); // Reload the list
          setOpenModal(false);
          setErrors({});
        } else {
          showSnackbar(response.errorDescription || 'Failed to create tenant', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving tenant:', err);
      showSnackbar('Failed to save tenant. Please try again.', 'error');
    }
  };

  const handleCancel = (): void => {
    setOpenModal(false);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<TenantFormData> = {};
    
    // Validate code
    const codeError = validateCode(formData.code, 'Code');
    if (codeError) newErrors.code = codeError;
    
    // Validate name
    const nameError = validateName(formData.name, 'Name');
    if (nameError) newErrors.name = nameError;
    
    // Validate URLs
    const frontendError = validateUrl(formData.frontendServer, 'Frontend Server');
    if (frontendError) newErrors.frontendServer = frontendError;
    
    const backendError = validateUrl(formData.backendApi, 'Backend API');
    if (backendError) newErrors.backendApi = backendError;
    
    const middlewareError = validateUrl(formData.middlewareApi, 'Middleware API');
    if (middlewareError) newErrors.middlewareApi = middlewareError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: keyof TenantFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Manage Tenants
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          data={tenants}
          columns={columns}
          onAdd={hasPermission(PERMISSIONS.CREATE_TENANT) ? handleAdd : undefined}
          onEdit={hasPermission(PERMISSIONS.EDIT_TENANT) ? handleEdit : undefined}
          onDelete={hasPermission(PERMISSIONS.DELETE_TENANT) ? handleDelete : undefined}
          onToggleStatus={hasPermission(PERMISSIONS.EDIT_TENANT) ? handleToggleStatus : undefined}
          title="Tenants"
          searchFields={['name', 'frontendServer', 'backendApi', 'middlewareApi']}
        />
      )}

      <Dialog open={openModal} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Add New Tenant'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => handleFieldChange('code', e.target.value.toLowerCase())}
              onBlur={() => {
                const error = validateCode(formData.code, 'Code');
                setErrors(prev => ({ ...prev, code: error }));
              }}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code}
              placeholder="Enter tenant code (e.g., tenantcode)"
              disabled={!!editingTenant}
            />
            
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              onBlur={() => {
                const error = validateName(formData.name, 'Name');
                setErrors(prev => ({ ...prev, name: error }));
              }}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Enter tenant name"
            />
            
            <TextField
              label="Frontend Server URL"
              value={formData.frontendServer}
              onChange={(e) => handleFieldChange('frontendServer', e.target.value)}
              onBlur={() => {
                const error = validateUrl(formData.frontendServer, 'Frontend Server');
                setErrors(prev => ({ ...prev, frontendServer: error }));
              }}
              fullWidth
              required
              error={!!errors.frontendServer}
              helperText={errors.frontendServer}
              placeholder="https://example.com"
            />
            
            <TextField
              label="Backend API URL"
              value={formData.backendApi}
              onChange={(e) => handleFieldChange('backendApi', e.target.value)}
              onBlur={() => {
                const error = validateUrl(formData.backendApi, 'Backend API');
                setErrors(prev => ({ ...prev, backendApi: error }));
              }}
              fullWidth
              required
              error={!!errors.backendApi}
              helperText={errors.backendApi}
              placeholder="https://api.example.com"
            />
            
            <TextField
              label="Middleware API URL"
              value={formData.middlewareApi}
              onChange={(e) => handleFieldChange('middlewareApi', e.target.value)}
              onBlur={() => {
                const error = validateUrl(formData.middlewareApi, 'Middleware API');
                setErrors(prev => ({ ...prev, middlewareApi: error }));
              }}
              fullWidth
              required
              error={!!errors.middlewareApi}
              helperText={errors.middlewareApi}
              placeholder="https://middleware.example.com"
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as TenantFormData['status'] })}
                label="Status"
                renderValue={(value) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status={value as 'Active' | 'Inactive' | 'Pending' | 'Maintenance'} size="small" />
                  </Box>
                )}
              >
                <MenuItem value="Active">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Active" size="small" />
                    <span>Active</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Inactive" size="small" />
                    <span>Inactive</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Pending">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Pending" size="small" />
                    <span>Pending</span>
                  </Box>
                </MenuItem>
                <MenuItem value="Maintenance">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip status="Maintenance" size="small" />
                    <span>Maintenance</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tenants; 